import { Button, Divider, InputGroup, TextArea, Card } from '@blueprintjs/core';
import { sms } from '@axo/shared/data-access/types';
import {
  CreatedAtTag,
  CreatorTag,
  SMSIcon,
  TransactionalIcon,
} from '../CommunicationItem';
import cardClasses from '../CommunicationItem/Card.module.css';
import { SelectQuote } from '../SelectQuote';
import { includesLoanQuoteTemplateKey } from '../templateKey';
import DatePicker from '../../DatePicker/DateTimePicker';
import { classNames } from '@axo/shared/util/dom';

interface ISMSCardEdit {
  SMS: sms.CreateSMS;
  to: string;
  setSMS: (SMS: sms.CreateSMS) => void;
  saveSMS: () => void;
  cancelSMS: () => void;
  template?: sms.SMSTemplate;
  marketCountry: string;
  applicationID: string;
}

export function SMSCardEdit({
  SMS,
  to,
  setSMS,
  saveSMS,
  cancelSMS,
  template,
  applicationID,
  marketCountry,
}: ISMSCardEdit) {
  const handleBodyChange = (value: string) => {
    setSMS({
      ...SMS,
      Body: value,
    });
  };

  const onLoanQuoteIDChange = (loanQuoteID: string | undefined) => {
    setSMS({
      ...SMS,
      LoanQuoteID: loanQuoteID,
    });
  };

  const handleFollowUpTimeChange = (value: string) => {
    setSMS({
      ...SMS,
      FollowupAt: value,
    });
  };

  const showSelectQuote =
    includesLoanQuoteTemplateKey(SMS.Body) || !!SMS.LoanQuoteID;

  return (
    <Card className={cardClasses.card}>
      <div className={cardClasses.topBar}>
        <SMSIcon inbound={false} />
        {SMS?.Transactional && <TransactionalIcon />}
        <CreatorTag creatorID={SMS.CreatorID} />
        {SMS.CreatedAt && (
          <CreatedAtTag label="Sent at:" createdAt={SMS.CreatedAt} />
        )}
        <div className={cardClasses.fill} />
      </div>
      <Divider />
      {showSelectQuote && (
        <>
          <SelectQuote
            applicationID={applicationID}
            marketCountry={marketCountry}
            onChange={onLoanQuoteIDChange}
          />
          <Divider />
        </>
      )}
      <div className={cardClasses.body}>
        <div className={cardClasses.formRow}>
          <span className={cardClasses.label}>To: </span>
          <InputGroup
            small={true}
            value={to}
            className={classNames(cardClasses.input, cardClasses.readOnly)}
            fill
            readOnly
          />
        </div>
        {template && (
          <div className={cardClasses.formRow}>
            <span className={cardClasses.label}>Name:</span>
            <span>{template?.Name}</span>
          </div>
        )}
        {template?.AllowsCustomContent && (
          <>
            <TextArea
              small={true}
              onChange={(e) => handleBodyChange(e.target.value)}
              value={SMS.Body}
              className={classNames(cardClasses.textArea, cardClasses.input)}
              placeholder="Write your custom SMS..."
              autoResize
            />
            <DatePicker
              onChange={(date) => handleFollowUpTimeChange(date.toString())}
              value={SMS.FollowupAt}
              buttonText="Set follow up time"
              dateInputProps={{
                highlightCurrentDay: true,
                showActionsBar: true,
                canClearSelection: true,
                minDate: new Date(),
                timePickerProps: { showArrowButtons: true },
              }}
            />
          </>
        )}
      </div>
      <Divider />
      <div className={cardClasses.bottomRow}>
        <div className={cardClasses.fill} />
        <Button icon="tick" onClick={saveSMS} outlined intent="primary">
          Confirm
        </Button>
        <Button
          icon="cross"
          onClick={cancelSMS}
          minimal
          outlined
          intent="danger"
        >
          Cancel
        </Button>
      </div>
    </Card>
  );
}
